import {
  Box,
  Grid,
  IconButton,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { CheckBox, colors, Modal } from 'design-system';
import { useEffect, useState } from 'react';
// Models
import { Quotation, QuotationUnit } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
// Redux related
import { defaultUnit } from 'data-layer-cr/dist/functions/quotations';
import { HelpCircle } from 'react-feather';
import { connect, ConnectedProps } from 'react-redux';
import {
  updatePricingForCoverages,
  updateQuotationPricesWithNewCoverages,
} from 'src/store/quotation/actions';
import { REQUEST_PRICING } from 'src/store/quotation/requestIdentifiers';
import { RootState } from 'src/store/rootReducer';
import { useStylesHelpModal } from '../IndividualDeductibles/DeductibleHelpModal';
import { getCoveragesLUCChange } from './helperLucFunctions';

const useStyles = makeStyles(() => {
  const nullBorder = 'none 0px transparent';
  return {
    divider: {
      width: '100%',
      borderTop: 'solid 1px #000000',
      opacity: '0.12',
      borderBottom: nullBorder,
      borderLeft: nullBorder,
      borderRight: nullBorder,
    },
    labelStyle: {
      fontWeight: 700,
      fontSize: '15px',
      lineHeight: '22px',
      color: '#0D2041',
    },
  };
});

export const LUCCoverage = ({
  unit,
  packageOption,
  savedQuotationUnit,
  currentQuotation,
  updateQuotationPricesWithNewCoveragesAction,
  updatePricingAction,
  currentSelectedPlan,
  onChangeLoadingPackage,
}: Props): JSX.Element => {
  const classes = useStyles();
  const classesHelpModal = useStylesHelpModal();
  const theme = useTheme();
  const smQuery = useMediaQuery(theme.breakpoints.down('md'));
  const [lucChecked, setLucChecked] = useState(
    unit.coverages.some((item) => item.coverageId === '0016'),
  );
  const [open, setOpen] = useState(false);
  const requestingPricing = useNetworkObserver(REQUEST_PRICING);

  useEffect(() => {
    if (!smQuery) setLucChecked(false);
  }, [currentSelectedPlan, smQuery]);

  useEffect(() => {
    setLucChecked(unit.coverages.some((item) => item.coverageId === '0016'));
  }, [unit, smQuery]);

  const handleChangeLUC = (check: boolean) => {
    onChangeLoadingPackage?.();
    setLucChecked(check);
    const newCoverages = getCoveragesLUCChange({
      unit,
      check,
      savedQuotationUnit,
      packageSelected: currentSelectedPlan,
    });
    updateQuotationPricesWithNewCoveragesAction(
      currentSelectedPlan,
      newCoverages,
      packageOption,
    );
    updatePricingAction(
      newCoverages,
      packageOption,
      defaultUnit(currentQuotation).deductibleOption,
      currentQuotation.productId,
      true,
      currentSelectedPlan,
    );
  };

  const handleCloseModal = () => setOpen(false);

  return (
    <Grid>
      <Modal
        data-testid="luc-help-modal"
        title={
          <Typography
            style={{
              color: smQuery ? '#0D2E68' : colors.white,
              paddingInline: smQuery ? '3rem' : '0',
            }}
            align="center"
          >
            ¿Qué es el Límite Único Combinado? (LUC)
          </Typography>
        }
        open={open}
        maxWidth="sm"
        onCloseRequested={handleCloseModal}
      >
        <Grid
          container
          item
          justifyContent="center"
          className={
            smQuery
              ? classesHelpModal.rootContainer
              : classesHelpModal.rootContainerLarge
          }
          style={{ paddingBottom: '1rem' }}
        >
          <Grid item container justifyContent="center" data-testid="first-page">
            <Grid item>
              <img
                src="/svg/luc_car.svg"
                alt="luc_car"
                style={{ width: '100%' }}
              />
            </Grid>

            <Grid
              item
              container
              spacing={1}
              justifyContent="center"
              style={{ maxWidth: '330px' }}
            >
              <Grid item xs={12} container>
                <Typography
                  align="center"
                  className={classesHelpModal.textTitle}
                  style={{ fontSize: 15, fontWeight: 400 }}
                >
                  Tendrás un monto de cobertura <strong>unificado</strong> para{' '}
                  <strong>lesiones corporales y daños a terceros.</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} container justifyContent="center">
                <Typography
                  align="center"
                  color="secondary"
                  className={classesHelpModal.textDescription}
                >
                  <strong>Por ejemplo:</strong> Si tu LUC es de $600,000, en
                  caso de accidente donde haya afectación de los vehículos y
                  personas que viajaban en estos, se sumará el total de todos
                  los gastos y se cubrirá un máximo de $600,000.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Modal>
      <Box display="flex" style={{ paddingTop: '12px' }}>
        <Box style={{ width: '25px' }}>
          <CheckBox
            data-testid="checkbox-LUC-id"
            checkName="test"
            defaultValue={lucChecked}
            onChange={handleChangeLUC}
            style={{
              width: '25px !Important',
            }}
            disabled={requestingPricing === 'requesting'}
          />
        </Box>
        <Box style={{ paddingRight: '5px' }}>
          <Typography
            className={classes.labelStyle}
            data-testid="checkbox-text"
          >
            Aplicar Límite Único Combinado
          </Typography>
        </Box>
        <Box
          data-testid="helper-button"
          style={{ width: '25px', textAlign: 'left' }}
          onClick={() => setOpen(true)}
        >
          <IconButton>
            <HelpCircle color={colors.bright_turquoise} size={24} />
          </IconButton>
        </Box>
      </Box>
    </Grid>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  currentQuotation: Quotation;
} => ({
  currentQuotation: state.quotation.currentQuotation,
});

const reduxActions = {
  updateQuotationPricesWithNewCoveragesAction:
    updateQuotationPricesWithNewCoverages,
  updatePricingAction: updatePricingForCoverages,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;
interface Props extends PropsFromRedux {
  unit: QuotationUnit;
  packageOption: number;
  savedQuotationUnit: QuotationUnit[];
  currentSelectedPlan: string;
  onChangeLoadingPackage?: () => void;
}

export default connector(LUCCoverage);
